import React from "react";
import { AlertCircle } from "lucide-react";

/* ───────── NoRefundsNotice ─────────
   Reusable policy notice shown above every registration / application
   submit button. Single source of truth so the wording stays uniform
   across the LMS RegisterPage and the Scholarship apply form.

   The notice is intentionally *informational rather than a hard gate*:
   it's positioned as the last thing the student reads before tapping
   Submit, and the submit button copy below confirms acceptance. This
   protects the org legally without adding a checkbox-tax that increases
   form abandonment.

   Variants:
     • theme="dark"  — for screens with a navy/dark background (LMS register)
     • theme="light" — for screens with a white/cream background (scholarship apply)
*/
export default function NoRefundsNotice({ theme = "dark", className = "" }) {
  const isDark = theme === "dark";
  const palette = isDark
    ? {
        bg: "rgba(239, 68, 68, 0.06)",
        border: "rgba(239, 68, 68, 0.28)",
        accent: "#fda4af",
        heading: "#ffffff",
        body: "rgba(255,255,255,0.78)",
        sub: "rgba(255,255,255,0.55)",
        chip: "rgba(255,255,255,0.05)",
      }
    : {
        bg: "rgba(239, 68, 68, 0.06)",
        border: "rgba(220, 38, 38, 0.28)",
        accent: "#b91c1c",
        heading: "#0b0b0d",
        body: "#3a3f49",
        sub: "#64748b",
        chip: "rgba(11,11,13,0.04)",
      };

  return (
    <div
      className={`rounded-[14px] p-4 md:p-5 border ${className}`}
      style={{ background: palette.bg, borderColor: palette.border }}
      data-testid="no-refunds-notice"
      role="region"
      aria-label="Refund policy"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
          style={{ background: palette.chip, color: palette.accent }}
          aria-hidden="true"
        >
          <AlertCircle size={15} />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1"
            style={{ color: palette.accent }}
          >
            Refund policy
          </div>
          <div
            className="text-[15px] font-semibold leading-snug"
            style={{ color: palette.heading }}
          >
            All registration fees are non-refundable.
          </div>
          <p
            className="text-[12.5px] leading-relaxed mt-1.5"
            style={{ color: palette.body }}
          >
            Once your payment is submitted, the amount cannot be refunded — partially or in full —
            for any reason, including withdrawal from the program, change of plan, missed sessions,
            duplicate transfers, or dissatisfaction with the content.
          </p>
          <p
            className="text-[11.5px] leading-relaxed mt-2"
            style={{ color: palette.sub }}
          >
            Please review the bank details and the fee amount carefully before transferring. If you
            are unsure about anything, contact our team
            <strong style={{ color: palette.heading }}> before</strong> sending payment — not after.
          </p>
        </div>
      </div>
    </div>
  );
}
